import { camelCase, isEmpty } from 'lodash'

import {
  AddressField,
  AddressFieldConfiguration,
  AddressModel,
} from '@marketplace-web/address/address-data'

import {
  ShipmentEntryType,
  DropOffTypeKey,
  LabelOptionsRewriteModel,
  DropOffType,
  LabelOptionsModel,
  DropOffsPointType,
  settingsUserInputEvent,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { PhoneRequirement } from '@marketplace-web/shipping/shipping-contact-data'

export const isReturnShipment = (labelOptionsRewrite?: LabelOptionsRewriteModel) => {
  return labelOptionsRewrite?.shipmentEntryType === ShipmentEntryType.Return
}

export const isPackageTypeLabelTypeFlow = (labelOptionsRewrite?: LabelOptionsRewriteModel) => {
  if (labelOptionsRewrite?.dropOffs) {
    const hasFreeReusablePackaging = labelOptionsRewrite.dropOffs.some(dropOff =>
      dropOff.options?.some(option => option.freeReusablePackagingAvailable),
    )

    const hasMultipleUniqueLabelTypes =
      [
        ...new Set(
          labelOptionsRewrite.dropOffs.flatMap(dropOff =>
            (dropOff.options ?? []).flatMap(option => option.labelTypes ?? []),
          ),
        ),
      ].length > 1

    return hasFreeReusablePackaging && hasMultipleUniqueLabelTypes
  }

  return false
}

export const isReusablePackagingFlowEnabled = (labelOptions, labelOptionsRewrite) => {
  const checkOldStructure = options => {
    return options?.dropOffTypes?.some(
      ({ key }) => key === DropOffTypeKey.ReusablePackage || key === DropOffTypeKey.MyOwnPackage,
    )
  }

  const checkNewStructure = options => {
    return options?.dropOffs?.some(dropOff =>
      dropOff.options?.some(option => option.freeReusablePackagingAvailable),
    )
  }

  return (
    checkOldStructure(labelOptions) ||
    checkNewStructure(labelOptions) ||
    checkOldStructure(labelOptionsRewrite) ||
    checkNewStructure(labelOptionsRewrite)
  )
}

export const isReusablePackagingFlowWithDimensionsEnabled = (labelOptions, labelOptionsRewrite) => {
  const findReusablePackageDimensions = options => {
    const dropOffWithReusablePackage = options?.dropOffs?.find(dropOff =>
      dropOff.options?.some(option => option.freeReusablePackagingAvailable),
    )

    const reusablePackageOption = dropOffWithReusablePackage?.options?.find(
      option => option.freeReusablePackagingAvailable,
    )

    return reusablePackageOption ? reusablePackageOption.formattedDimensions : null
  }

  const fromOldStructure = options =>
    options?.dropOffTypes?.some(
      ({ key }) => key === DropOffTypeKey.ReusablePackage || key === DropOffTypeKey.MyOwnPackage,
    )

  const dimensionsFromNewStructure =
    findReusablePackageDimensions(labelOptions) ||
    findReusablePackageDimensions(labelOptionsRewrite)

  return {
    enabled:
      fromOldStructure(labelOptions) ||
      fromOldStructure(labelOptionsRewrite) ||
      Boolean(dimensionsFromNewStructure),
    dimensions: dimensionsFromNewStructure,
  }
}

export const isTypeMailbox = (labelOptionsRewrite?: LabelOptionsRewriteModel) => {
  return labelOptionsRewrite?.dropOffs.some(dropOff =>
    dropOff.options.some(option => option.type === DropOffType.Mailbox),
  )
}

export const isDropOffMapEnabled = (
  labelOptions?: LabelOptionsModel,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
) => {
  if (labelOptionsRewrite) {
    return labelOptionsRewrite.dropOffs.some(dropOff => dropOff.providesNearbyDropOffPoints)
  }

  return labelOptions?.dropOffMapEnabled
}

export const shouldShowPickupDateOld = (
  isHomePickupSelected: boolean,
  labelOptions?: LabelOptionsModel,
): boolean => {
  if (!labelOptions) return false
  const isOldDropOffFlow = labelOptions.dropOffTypes?.some(
    ({ pickUpDateShown }: { pickUpDateShown: boolean }) => pickUpDateShown,
  )
  const isOldDropOffsFlow =
    labelOptions.dropOffs &&
    labelOptions.dropOffs.length === 1 &&
    labelOptions.dropOffs[0]?.type === DropOffsPointType.Home

  return isOldDropOffFlow || isOldDropOffsFlow || isHomePickupSelected
}

export const shouldShowNewFlow = (
  isHomePickupSelected: boolean,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
): { newFlow: boolean; newFlowDetails: boolean } => {
  if (!labelOptionsRewrite) return { newFlow: false, newFlowDetails: false }

  const newDropOffs = labelOptionsRewrite.dropOffs

  const isNewDropOffFlow =
    Array.isArray(newDropOffs) &&
    newDropOffs.some(dropOff => {
      const options = dropOff?.options ?? []

      const hasPickupRequirements = options.some(
        option => option?.requiresPickupTime || option?.requiresPickupDate,
      )

      const optionTypes = new Set(options.map(option => option?.type))

      const hasHomeAndMailbox =
        optionTypes.has(DropOffType.Home) && optionTypes.has(DropOffType.Mailbox)

      const allOptions = newDropOffs.flatMap(newDropOff => newDropOff.options ?? [])

      const singleHomeOption = allOptions.length === 1 && allOptions[0]?.type === DropOffType.Home

      return (hasPickupRequirements && hasHomeAndMailbox) || singleHomeOption
    })

  const isNewDropOffsFlow =
    newDropOffs &&
    newDropOffs.length > 1 &&
    newDropOffs.some(dropOff => dropOff.type === DropOffType.Home) &&
    newDropOffs.some(dropOff => dropOff.type === DropOffType.Point)

  return {
    newFlow: isNewDropOffFlow && !isHomePickupSelected,
    newFlowDetails: isNewDropOffsFlow && isHomePickupSelected,
  }
}

export const getIsContactlessDropoffFlow = (
  labelOptionsRewrite?: LabelOptionsRewriteModel,
  labelOptions?: LabelOptionsModel,
) => {
  const isContactlessInRewrite = labelOptionsRewrite?.dropOffs?.some((dropOff: any) =>
    dropOff.options?.some((option: any) => option.contactless === true),
  )

  const isContactlessInOld = labelOptions?.dropOffTypes?.some(
    (type: any) => type.key === DropOffTypeKey.ContactlessDropoff,
  )

  return isContactlessInRewrite || isContactlessInOld
}

export const getIsAddressComplete = (
  address: AddressModel | null | undefined,
  fieldConfiguration: Array<AddressFieldConfiguration> | null | undefined,
) => {
  if (!address) return false
  if (!fieldConfiguration) return false

  const requiredFields = fieldConfiguration.filter(
    ({ field, required }) => required && isEmpty(address[camelCase(field)]),
  )

  return requiredFields.length === 0
}

const addressFieldConfig: Array<AddressFieldConfiguration> = [
  {
    field: AddressField.Name,
    required: true,
  },
  {
    field: AddressField.Country,
  },
  {
    field: AddressField.Line1,
    required: true,
  },
  {
    field: AddressField.Line2,
  },
  {
    field: AddressField.PostalCode,
    required: true,
  },
  {
    field: AddressField.City,
    required: true,
  },
]

export const addressFieldConfiguration = addressFieldConfig.map(field => {
  return {
    field: field.field,
    required: field.required,
    trackingEvent: settingsUserInputEvent({
      screen: 'get_shipping_label',
      target: field.field,
      isValid: true,
    }),
  }
})

export const getDropOffHour = (dropOffTime?: string | null) => {
  const timeMatch = dropOffTime?.match(/T(\d{2}:\d{2})/)

  return timeMatch ? timeMatch[1] : ''
}

export const isDateObjectPresent = (dateObj, dateArray) => {
  if (!dateArray || dateArray.length === 0) return null
  if (!dateObj) return null

  return dateArray.some(
    date =>
      date.collection === dateObj.collection &&
      date.collectionTo === dateObj.collectionTo &&
      date.dropOffTo === dateObj.dropOffTo,
  )
}

export const isDigitalLabelFlowEnabled = (
  labelOptions?: LabelOptionsModel,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
) => {
  if (labelOptionsRewrite?.dropOffs) {
    return (
      !!labelOptionsRewrite?.dropOffs &&
      [
        ...new Set(
          labelOptionsRewrite.dropOffs.flatMap(dropOff =>
            (dropOff.options ?? []).flatMap(option => option.labelTypes ?? []),
          ),
        ),
      ].length > 1
    )
  }

  if (Array.isArray(labelOptions?.availableLabelTypes)) {
    return labelOptions.availableLabelTypes.length > 1
  }

  return false
}

export const isOldDropOffPointTypeFlow = (labelOptions?: LabelOptionsModel) => {
  return Array.isArray(labelOptions?.dropOffs) && labelOptions.dropOffs.length > 1
}

export const isDropOffTypeFlow = (
  labelOptionsRewrite?: LabelOptionsRewriteModel,
  labelOptions?: LabelOptionsModel,
) => {
  if (labelOptionsRewrite?.dropOffs) {
    return Array.isArray(labelOptionsRewrite?.dropOffs) && labelOptionsRewrite.dropOffs.length > 1
  }

  return labelOptions?.dropOffTypes && labelOptions?.dropOffTypes.length > 1
}

export const isShippingContactDisabled = (
  labelOptionsRewrite?: LabelOptionsRewriteModel,
  labelOptions?: LabelOptionsModel,
) => {
  if (labelOptionsRewrite) {
    return (
      !labelOptionsRewrite?.sender.phoneNumberRequirement ||
      labelOptionsRewrite?.sender.phoneNumberRequirement === PhoneRequirement.Disabled
    )
  }

  return (
    !labelOptions?.sender.phoneNumberRequirement ||
    labelOptions?.sender.phoneNumberRequirement === PhoneRequirement.Disabled
  )
}

export const isHomePickupSelected = (
  selectedDropOffPointType: number | null,
  selectedDropOffTypeKey: string | number | null,
) =>
  selectedDropOffPointType === DropOffsPointType.Home || selectedDropOffTypeKey === DropOffType.Home

export const shouldShowPickupDateNew = (
  isHomeDeliveryPickupSelected: boolean,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
): { newFlow: boolean; newFlowDetails: boolean } => {
  if (!labelOptionsRewrite) return { newFlow: false, newFlowDetails: false }

  const newDropOffs = labelOptionsRewrite.dropOffs

  const isNewDropOffFlow =
    Array.isArray(newDropOffs) &&
    newDropOffs.some(dropOff => {
      const options = dropOff?.options ?? []

      const hasPickupRequirements = options.some(
        option => option?.requiresPickupTime || option?.requiresPickupDate,
      )

      const optionTypes = new Set(options.map(option => option?.type))

      const hasHomeAndMailbox =
        optionTypes.has(DropOffType.Home) && optionTypes.has(DropOffType.Mailbox)

      const allOptions = newDropOffs.flatMap(newDropOff => newDropOff.options ?? [])

      const singleHomeOption = allOptions.length === 1 && allOptions[0]?.type === DropOffType.Home

      return (hasPickupRequirements && hasHomeAndMailbox) || singleHomeOption
    })

  const isNewDropOffsFlow =
    newDropOffs &&
    newDropOffs.length > 1 &&
    newDropOffs.some(dropOff => dropOff.type === DropOffType.Home) &&
    newDropOffs.some(dropOff => dropOff.type === DropOffType.Point)

  return {
    newFlow: isNewDropOffFlow && !isHomeDeliveryPickupSelected,
    newFlowDetails: isNewDropOffsFlow && isHomeDeliveryPickupSelected,
  }
}

export const isMultipleDropOffOptions = (
  labelOptions?: LabelOptionsModel,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
) => {
  if (labelOptionsRewrite?.dropOffs) {
    return labelOptionsRewrite.dropOffs.some(
      dropOff => dropOff.options && dropOff.options.length > 1,
    )
  }

  return !!(labelOptions?.dropOffTypes && labelOptions.dropOffTypes.length > 1)
}

export const getDefaultselectedDropOffPointType = (labelOptions?: LabelOptionsModel) =>
  labelOptions?.dropOffs?.length === 1 ? labelOptions?.dropOffs[0]?.type : undefined

export const getDropOffPointType = (
  labelOptions?: LabelOptionsModel,
  labelOptionsRewrite?: LabelOptionsRewriteModel,
  selectedDropOffTypeKey?: number | string | null,
  selectedDropOffPointType?: number | null,
) => {
  if (
    labelOptionsRewrite &&
    (selectedDropOffTypeKey === DropOffType.Point ||
      selectedDropOffTypeKey === DropOffType.Home ||
      selectedDropOffTypeKey === DropOffType.Mailbox)
  ) {
    return selectedDropOffTypeKey
  }

  if (labelOptions && selectedDropOffPointType) {
    return selectedDropOffPointType
  }

  const defaultSelectedDropOffPointType = getDefaultselectedDropOffPointType()

  if (labelOptions && defaultSelectedDropOffPointType) {
    return defaultSelectedDropOffPointType
  }

  return null
}
